import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaBox, FaClipboardList, FaMoneyBillWave } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';
import { useTranslation } from 'react-i18next';
import './AdminSystemReports.css';

const AdminSystemReports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFarmers: 0,
    totalConsumers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0
  });
  const [userGrowth, setUserGrowth] = useState([]);
  const [orderStats, setOrderStats] = useState([]);
  const [reportType, setReportType] = useState('overview');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== 'admin') {
          navigate('/');
        } else {
          fetchReportsData();
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        setLoading(false);
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchReportsData = async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats
      const statsResponse = await api.get('/admin/dashboard');
      setStats(statsResponse.data);

      // Fetch user growth
      const growthResponse = await api.get('/admin/analytics/user-growth');
      setUserGrowth(growthResponse.data || []);

      // Fetch order stats
      const ordersResponse = await api.get('/admin/analytics/orders');
      setOrderStats(ordersResponse.data || []);
    } catch (error) {
      console.error('Error fetching reports data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `Rs. ${amount.toLocaleString('en-NP')}`;
  };

  const renderUserGrowthChart = () => {
    if (userGrowth.length === 0) return <div className="admin-system-reports-chart-placeholder">{t('adminSystemReports.noData')}</div>;
    
    const maxValue = Math.max(
      ...userGrowth.map(m => Math.max(m.farmers, m.consumers))
    );

    return (
      <div className="admin-system-reports-chart-container">
        <div className="admin-system-reports-chart-bars">
          {userGrowth.map((month, index) => (
            <div key={index} className="admin-system-reports-chart-bar-group">
              <div className="admin-system-reports-chart-bar-wrapper">
                <div
                  className="admin-system-reports-chart-bar farmers"
                  style={{ height: `${(month.farmers / maxValue) * 100}%` }}
                  title={`Farmers: ${month.farmers}`}
                >
                  <span className="admin-system-reports-bar-value">{month.farmers}</span>
                </div>
                <div
                  className="admin-system-reports-chart-bar consumers"
                  style={{ height: `${(month.consumers / maxValue) * 100}%` }}
                  title={`Consumers: ${month.consumers}`}
                >
                  <span className="admin-system-reports-bar-value">{month.consumers}</span>
                </div>
              </div>
              <div className="admin-system-reports-chart-label">{month.month}</div>
            </div>
          ))}
        </div>
        <div className="admin-system-reports-chart-legend">
          <div className="admin-system-reports-legend-item">
            <span className="admin-system-reports-legend-color farmers"></span>
            <span>Farmers</span>
          </div>
          <div className="admin-system-reports-legend-item">
            <span className="admin-system-reports-legend-color consumers"></span>
            <span>Consumers</span>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderStatsChart = () => {
    if (orderStats.length === 0) return <div className="admin-system-reports-chart-placeholder">{t('adminSystemReports.noData')}</div>;
    
    const maxValue = Math.max(
      ...orderStats.map(d => Math.max(d.completed, d.pending, d.rejected))
    );

    return (
      <div className="admin-system-reports-chart-container">
        <div className="admin-system-reports-chart-bars">
          {orderStats.map((day, index) => (
            <div key={index} className="admin-system-reports-chart-bar-group">
              <div className="admin-system-reports-chart-bar-wrapper">
                <div
                  className="admin-system-reports-chart-bar completed"
                  style={{ height: `${(day.completed / maxValue) * 100}%` }}
                  title={`Completed: ${day.completed}`}
                >
                  <span className="admin-system-reports-bar-value">{day.completed}</span>
                </div>
                <div
                  className="admin-system-reports-chart-bar pending"
                  style={{ height: `${(day.pending / maxValue) * 100}%` }}
                  title={`Pending: ${day.pending}`}
                >
                  <span className="admin-system-reports-bar-value">{day.pending}</span>
                </div>
                <div
                  className="admin-system-reports-chart-bar rejected"
                  style={{ height: `${(day.rejected / maxValue) * 100}%` }}
                  title={`Rejected: ${day.rejected}`}
                >
                  <span className="admin-system-reports-bar-value">{day.rejected}</span>
                </div>
              </div>
              <div className="admin-system-reports-chart-label">{day.day}</div>
            </div>
          ))}
        </div>
        <div className="admin-system-reports-chart-legend">
          <div className="admin-system-reports-legend-item">
            <span className="admin-system-reports-legend-color completed"></span>
            <span>Completed</span>
          </div>
          <div className="admin-system-reports-legend-item">
            <span className="admin-system-reports-legend-color pending"></span>
            <span>Pending</span>
          </div>
          <div className="admin-system-reports-legend-item">
            <span className="admin-system-reports-legend-color rejected"></span>
            <span>Rejected</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-system-reports-content">
          <div className="admin-system-reports-loading-state">
            <div className="admin-system-reports-spinner"></div>
              <p>{t('adminSystemReports.loading')}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-system-reports-content">
        {/* Page Header */}
        <div className="admin-system-reports-page-header">
          <div>
            <h1 className="admin-system-reports-page-title">{t('adminSystemReports.title')}</h1>
            <p className="admin-system-reports-page-subtitle">{t('adminSystemReports.subtitle')}</p>
          </div>
          <div className="admin-system-reports-report-tabs">
            <button
              className={`admin-system-reports-tab-btn ${reportType === 'overview' ? 'active' : ''}`}
              onClick={() => setReportType('overview')}
            >
              {t('adminSystemReports.overview')}
            </button>
            <button
              className={`admin-system-reports-tab-btn ${reportType === 'users' ? 'active' : ''}`}
              onClick={() => setReportType('users')}
            >
              {t('adminSystemReports.users')}
            </button>
            <button
              className={`admin-system-reports-tab-btn ${reportType === 'orders' ? 'active' : ''}`}
              onClick={() => setReportType('orders')}
            >
              {t('adminSystemReports.orders')}
            </button>
          </div>
        </div>

        {/* Overview Report */}
        {reportType === 'overview' && (
          <>
            {/* Key Metrics */}
            <div className="admin-system-reports-metrics-grid">
              <div className="admin-system-reports-metric-card">
                <div className="admin-system-reports-metric-icon"><FaUsers aria-hidden /></div>
                <div className="admin-system-reports-metric-content">
                  <div className="admin-system-reports-metric-value">{stats.totalUsers}</div>
                  <div className="admin-system-reports-metric-label">{t('adminSystemReports.totalUsers')}</div>
                  <div className="admin-system-reports-metric-breakdown">
                    {stats.totalFarmers} Farmers • {stats.totalConsumers} Consumers
                  </div>
                </div>
              </div>
              <div className="admin-system-reports-metric-card">
                <div className="admin-system-reports-metric-icon"><FaBox aria-hidden /></div>
                <div className="admin-system-reports-metric-content">
                  <div className="admin-system-reports-metric-value">{stats.totalProducts}</div>
                  <div className="admin-system-reports-metric-label">{t('adminSystemReports.totalProducts')}</div>
                </div>
              </div>
              <div className="admin-system-reports-metric-card">
                <div className="admin-system-reports-metric-icon"><FaClipboardList aria-hidden /></div>
                <div className="admin-system-reports-metric-content">
                  <div className="admin-system-reports-metric-value">{stats.totalOrders}</div>
                  <div className="admin-system-reports-metric-label">{t('adminSystemReports.totalOrders')}</div>
                </div>
              </div>
              <div className="admin-system-reports-metric-card highlight">
                <div className="admin-system-reports-metric-icon"><FaMoneyBillWave aria-hidden /></div>
                <div className="admin-system-reports-metric-content">
                  <div className="admin-system-reports-metric-value">{formatCurrency(stats.totalRevenue)}</div>
                  <div className="admin-system-reports-metric-label">{t('adminSystemReports.totalRevenue')}</div>
                  <div className="admin-system-reports-metric-breakdown">
                    Avg: {formatCurrency(stats.averageOrderValue || 0)}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="admin-system-reports-quick-stats-grid">
              <div className="admin-system-reports-quick-stat-card">
                <h3>{t('adminSystemReports.userGrowth')}</h3>
                {renderUserGrowthChart()}
              </div>
              <div className="admin-system-reports-quick-stat-card">
                <h3>{t('adminSystemReports.orderStats')}</h3>
                {renderOrderStatsChart()}
              </div>
            </div>
          </>
        )}

        {/* Users Report */}
        {reportType === 'users' && (
          <div className="admin-system-reports-report-section">
            <div className="admin-system-reports-report-card">
              <h2>{t('adminSystemReports.userGrowth')}</h2>
              <p className="admin-system-reports-report-description">Monthly registration trends for farmers and consumers</p>
              {renderUserGrowthChart()}
            </div>
            <div className="admin-system-reports-report-card">
              <h2>{t('adminSystemReports.userDistribution')}</h2>
              <div className="admin-system-reports-distribution-grid">
                <div className="admin-system-reports-distribution-item">
                  <div className="admin-system-reports-distribution-value">{stats.totalFarmers}</div>
                  <div className="admin-system-reports-distribution-label">Farmers</div>
                  <div className="admin-system-reports-distribution-percentage">
                    {stats.totalUsers > 0 
                      ? ((stats.totalFarmers / stats.totalUsers) * 100).toFixed(1)
                      : 0}%
                  </div>
                </div>
                <div className="admin-system-reports-distribution-item">
                  <div className="admin-system-reports-distribution-value">{stats.totalConsumers}</div>
                  <div className="admin-system-reports-distribution-label">Consumers</div>
                  <div className="admin-system-reports-distribution-percentage">
                    {stats.totalUsers > 0 
                      ? ((stats.totalConsumers / stats.totalUsers) * 100).toFixed(1)
                      : 0}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orders Report */}
        {reportType === 'orders' && (
          <div className="admin-system-reports-report-section">
            <div className="admin-system-reports-report-card">
              <h2>{t('adminSystemReports.orderStats')}</h2>
              <p className="admin-system-reports-report-description">Daily order status breakdown</p>
              {renderOrderStatsChart()}
            </div>
            <div className="admin-system-reports-report-card">
              <h2>{t('adminSystemReports.revenueSummary')}</h2>
              <div className="admin-system-reports-revenue-summary">
                <div className="admin-system-reports-revenue-item">
                  <div className="admin-system-reports-revenue-label">{t('adminSystemReports.totalRevenue')}</div>
                  <div className="admin-system-reports-revenue-value">{formatCurrency(stats.totalRevenue)}</div>
                </div>
                <div className="admin-system-reports-revenue-item">
                  <div className="admin-system-reports-revenue-label">{t('adminSystemReports.totalOrders')}</div>
                  <div className="admin-system-reports-revenue-value">{stats.totalOrders}</div>
                </div>
                <div className="admin-system-reports-revenue-item highlight">
                  <div className="admin-system-reports-revenue-label">{t('adminSystemReports.averageOrderValue')}</div>
                  <div className="admin-system-reports-revenue-value">
                    {formatCurrency(stats.averageOrderValue || (stats.totalOrders > 0 ? stats.totalRevenue / stats.totalOrders : 0))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSystemReports;

